import { Host, h } from "@stencil/core";
import { changeForm, createFormFieldState, handlesValidationForField, toChildState, validate } from "../../utils/FormDefinition";
import { clone, toArray, toString } from "../../utils/utils";
import { toSingleError } from "../../utils/RenderInfo";
import { FallbackRenderInfo } from "../../utils/FallbackRenderInfo";
export class ApieForm {
    constructor() {
        this.instantiated = false;
        this.formDefinition = undefined;
        this.action = window.location.href;
        this.method = 'post';
        this.submitLabel = 'Submit';
        this.csrfToken = null;
        this.initialValue = undefined;
        this.value = {};
        this.internalState = {};
        this.validationErrors = {};
        this.definitionId = undefined;
        this.supportsMultipart = false;
        this.debugMode = false;
        this.renderInfo = new FallbackRenderInfo();
        this.polymorphicColumnName = undefined;
        this.polymorphicFormDefinition = undefined;
    }
    onInternalStateUpdate(fieldNamePath, value) {
        console.log(fieldNamePath, value);
        if (fieldNamePath.length === 0) {
            // todo ensure {}
            this.internalState = value;
            return;
        }
        let ptr = this.internalState;
        let fieldName = fieldNamePath[0];
        if (fieldNamePath.length === 1) {
            ptr[fieldName] = value;
            this.internalState = Object.assign({}, this.internalState);
            return;
        }
        while (fieldNamePath.length > 1) {
            fieldName = fieldNamePath.shift();
            if (!Object.prototype.hasOwnProperty.call(ptr, fieldName)) {
                ptr[fieldName] = {};
            }
            ptr = ptr[fieldName];
        }
        fieldName = fieldNamePath.shift();
        ptr[fieldName] = value;
        this.internalState = Object.assign({}, this.internalState);
    }
    onFieldUpdate(fieldNamePath, value) {
        if (fieldNamePath.length === 0) {
            // todo ensure {}
            this.value = value;
            return;
        }
        let ptr = this.value;
        let fieldName = fieldNamePath[0];
        if (fieldNamePath.length === 1) {
            ptr[fieldName] = value;
            this.value = Object.assign({}, this.value);
            return;
        }
        while (fieldNamePath.length > 1) {
            fieldName = fieldNamePath.shift();
            if (!Object.prototype.hasOwnProperty.call(ptr, fieldName) || ptr[fieldName] === null || typeof ptr[fieldName] !== 'object') {
                ptr[fieldName] = {};
            }
            ptr = ptr[fieldName];
        }
        fieldName = fieldNamePath.shift();
        ptr[fieldName] = value;
        this.value = Object.assign({}, this.value);
    }
    getItem(fieldNamePath) {
        let fieldNamePathCopy = fieldNamePath.slice(0);
        let ptr = this.value;
        let fieldName;
        while (fieldNamePathCopy.length > 0) {
            fieldName = fieldNamePathCopy.shift();
            ptr = ptr[fieldName] ? ptr[fieldName] : [];
        }
        return ptr;
    }
    onAddItemList(fieldNamePath) {
        const ptr = toArray(this.getItem(fieldNamePath)).slice(0);
        ptr.push(null);
        this.onFieldUpdate(fieldNamePath, ptr);
    }
    removeFromList(fieldNamePath, index) {
        const ptr = toArray(this.getItem(fieldNamePath)).slice(0);
        ptr.splice(index, 1);
        this.onFieldUpdate(fieldNamePath, ptr);
    }
    renderRootField(formField, formDefinition) {
        const formFieldState = createFormFieldState(formField, formDefinition);
        return this.renderInfo.renderListOrMapRow({
            mappingKey: formField.name,
            isMap: true,
        }, this.renderField(formFieldState, []));
    }
    async onDefinitionIdChange(newValue) {
        const definition = await new Promise((resolve, reject) => {
            const id = setInterval(() => {
                if (!this.instantiated || this.definitionId !== newValue) {
                    clearInterval(id);
                    reject(new Error('definitionId changed or component destroyed'));
                }
                const definition = document.getElementById(this.definitionId);
                if (definition) {
                    clearInterval(id);
                    resolve(definition);
                }
            });
        });
        this.formDefinition = await definition.getDefinition();
    }
    updateDefinitionIdOnPolymorphicDefinition() {
        if (!this.polymorphicColumnName) {
            return;
        }
        this.definitionId = this.value[this.polymorphicColumnName] ? toString(this.value[this.polymorphicColumnName]) : undefined;
    }
    renderPolymorphicSelection() {
        if (!this.polymorphicFormDefinition) {
            return [];
        }
        return this.renderInfo.renderSingleInput(['polymorphic_select', 'select'], {
            name: this.polymorphicColumnName,
            label: 'type',
            value: this.value[this.polymorphicColumnName],
            disabled: !this.polymorphicColumnName || Object.entries(this.polymorphicFormDefinition).length === 0,
            valueChanged: (newValue) => {
                this.value[this.polymorphicColumnName] = newValue;
                this.updateDefinitionIdOnPolymorphicDefinition();
                this.value = Object.assign({}, this.value);
            },
            touched: !this.polymorphicColumnName,
            onTouched: () => { },
            additionalSettings: {
                options: Object.entries(this.polymorphicFormDefinition)
                    .map(([value, name]) => { return { name, value }; }),
            },
            validationResult: {
                valid: true,
                messages: []
            },
            serverValidationError: toSingleError(this.validationErrors[this.polymorphicColumnName], this.value[this.polymorphicColumnName], this.initialValue[this.polymorphicColumnName]),
            renderInfo: this.renderInfo,
            currentFieldWrapper: this.renderInfo.createFieldWrapper(),
        });
    }
    formName(prefixes) {
        if (prefixes.length === 0) {
            return 'form';
        }
        return 'form[' + prefixes.join('][') + ']';
    }
    renderField(state, prefixes, key = null) {
        var _a, _b, _c, _d, _e, _f, _g;
        const newPrefix = [...prefixes, state.form.name];
        const validationResult = validate(state.value, state.form.constraints);
        if (state.form.fieldType === 'single') {
            return h("apie-single-input", { key: key !== null && key !== void 0 ? key : state.form.name, name: this.formName(newPrefix), types: state.form.types.join(','), label: state.form.label, value: state.value, allowsNull: state.form.allowsNull, internalState: state.internalState, onInternalStateChanged: (ev) => this.onInternalStateUpdate(newPrefix.slice(0), ev), emptyStringAllowed: state.form.emptyStringAllowed, required: state.form.required, renderInfo: this.renderInfo, additionalSettings: state.form.additionalSettings, onTriggerChange: (ev) => { this.onFieldUpdate(newPrefix.slice(0), ev.detail.value); }, validationResult: validationResult, serverValidationError: toSingleError(state.validationErrors, state.value, state.initialValue) });
        }
        if (state.form.fieldType === 'group') {
            const subElements = [];
            let index = 0;
            const unmappedValidationErrors = new Set(Object.keys((_a = state.validationErrors) !== null && _a !== void 0 ? _a : {}));
            for (let formField of state.form.fields) {
                let newState = toChildState(formField, state);
                if (handlesValidationForField(formField)) {
                    unmappedValidationErrors.delete(formField.name);
                }
                subElements.push(this.renderField(newState, newPrefix, index));
                index++;
            }
            subElements.push(this.renderInfo.renderUnmappedErrors(unmappedValidationErrors, state.validationErrors));
            return this.renderInfo.renderFormGroup({
                name: state.form.name,
                label: state.form.label,
                value: state.value,
            }, subElements, key);
        }
        if (state.form.fieldType === 'map') {
            const subElements = [];
            const formName = this.formName(newPrefix);
            const list = Array.from(Object.entries((_b = state.value) !== null && _b !== void 0 ? _b : {}));
            const unmappedValidationErrors = new Set(Object.keys((_c = state.validationErrors) !== null && _c !== void 0 ? _c : {}));
            for (let index = 0; index < list.length; index++) {
                const subFormField = clone(state.form.subField);
                subFormField.name = String(list[index][0]);
                if (handlesValidationForField(subFormField)) {
                    unmappedValidationErrors.delete(subFormField.name);
                }
                let newState = toChildState(subFormField, state);
                subElements.push(this.renderField(newState, newPrefix, index));
            }
            subElements.push(this.renderInfo.renderUnmappedErrors(unmappedValidationErrors, state.validationErrors));
            return h("apie-form-map", { key: key !== null && key !== void 0 ? key : state.form.name, subElements: subElements, name: formName, types: state.form.types.join(','), label: state.form.label, value: state.value, renderInfo: this.renderInfo, onTriggerChange: (ev) => { ev.detail.name === formName && this.onFieldUpdate(newPrefix, ev.detail.value); } });
        }
        if (state.form.fieldType === 'list') {
            const subElements = [];
            const list = toArray(state.value).slice(0);
            const unmappedValidationErrors = new Set(Object.keys((_d = state.validationErrors) !== null && _d !== void 0 ? _d : {}));
            for (let index = 0; index < list.length; index++) {
                const subFormField = clone(state.form.subField);
                subFormField.name = String(index);
                if (handlesValidationForField(subFormField)) {
                    unmappedValidationErrors.delete(subFormField.name);
                }
                let newState = toChildState(subFormField, state);
                subElements.push(this.renderInfo.renderListOrMapRow({
                    mappingKey: index,
                    isMap: false,
                    onRowRemove: () => this.removeFromList(newPrefix.slice(0), index),
                }, this.renderField(newState, newPrefix, index)));
            }
            subElements.push(this.renderInfo.renderAddItemToList({
                mappingKey: '__add' + (key !== null && key !== void 0 ? key : 'unknown'),
                isMap: true,
                label: 'Add',
                disabled: false,
                onRowAdd: () => this.onAddItemList(newPrefix.slice(0))
            }));
            subElements.push(this.renderInfo.renderUnmappedErrors(unmappedValidationErrors, state.validationErrors));
            return this.renderInfo.renderFormGroup({
                name: state.form.name,
                label: state.form.label,
                value: state.value,
            }, subElements, key !== null && key !== void 0 ? key : state.form.name);
        }
        if (state.form.fieldType === 'split') {
            const formName = this.formName(newPrefix);
            const selected = (_f = (_e = state.internalState) === null || _e === void 0 ? void 0 : _e._split) !== null && _f !== void 0 ? _f : null;
            let selectedField = null;
            for (let subField of state.form.subFields) {
                if (subField.value === selected) {
                    selectedField = subField;
                    break;
                }
            }
            return [
                h("apie-form-select", { key: key !== null && key !== void 0 ? key : state.form.name, name: formName, label: state.form.label, value: state.value, options: state.form.subFields, renderInfo: this.renderInfo, internalState: (_g = state.internalState) !== null && _g !== void 0 ? _g : {}, onTriggerChange: (ev) => { ev.detail.name === formName && this.onFieldUpdate(newPrefix, ev.detail.value); }, onTriggerInternalState: (ev) => { ev.detail.name === formName && this.onInternalStateUpdate(newPrefix, ev.detail.value); } }),
                selectedField && this.renderField(changeForm(selectedField.definition, state), prefixes, key + '_subform')
            ];
        }
        console.log(state);
        return h("div", null);
    }
    renderSubmitButton() {
        return this.renderInfo.renderSubmitButton({
            label: this.submitLabel,
            disabled: false
        });
    }
    getCalculatedAction() {
        return String(this.action || '').replace(/\{([a-zA-Z0-9]+)\}/g, (_match, cp1) => {
            var _a;
            return String((_a = this.value[cp1]) !== null && _a !== void 0 ? _a : '');
        });
    }
    connectedCallback() {
        this.instantiated = true;
        if (!this.initialValue) {
            this.initialValue = clone(this.value);
        }
        if (this.polymorphicFormDefinition) {
            this.updateDefinitionIdOnPolymorphicDefinition();
        }
        if (this.definitionId) {
            this.onDefinitionIdChange(this.definitionId);
        }
    }
    disconnectedCallback() {
        this.instantiated = false;
    }
    render() {
        var _a, _b;
        const formDefinition = (_a = this.formDefinition) !== null && _a !== void 0 ? _a : null;
        if (!formDefinition) {
            return h(Host, null, this.renderPolymorphicSelection());
        }
        const state = {
            form: formDefinition,
            csrfToken: this.csrfToken,
            value: this.value,
            initialValue: this.initialValue,
            internalState: this.internalState,
            validationErrors: this.validationErrors,
        };
        const fields = [];
        const unmappedValidationErrors = new Set(Object.keys((_b = state.validationErrors) !== null && _b !== void 0 ? _b : {}));
        formDefinition.fields.forEach((formField) => {
            fields.push(this.renderRootField(formField, state));
            if (handlesValidationForField(formField)) {
                unmappedValidationErrors.delete(formField.name);
            }
        });
        fields.push(this.renderInfo.renderUnmappedErrors(unmappedValidationErrors, state.validationErrors));
        return (h(Host, null, this.debugMode && h("pre", null, JSON.stringify(this.value, (_key, value) => {
            if (value instanceof File) {
                return { name: value.name, type: value.type, lastModified: value.lastModified, size: value.size };
            }
            return value;
        }, 4)), this.renderPolymorphicSelection(), h("slot", null), this.renderInfo.renderFormGroup({
            name: '',
            value: this.value,
        }, fields, null), h("form", { action: this.getCalculatedAction(), method: this.method, enctype: this.supportsMultipart ? 'multipart/form-data' : 'application/x-www-form-urlencoded' }, h("apie-render-types", { value: this.value, csrfToken: this.csrfToken, internalState: this.internalState, supportsMultipart: this.supportsMultipart }), this.renderSubmitButton())));
    }
    static get is() { return "apie-form"; }
    static get originalStyleUrls() {
        return {
            "$": ["apie-form.css"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["apie-form.css"]
        };
    }
    static get properties() {
        return {
            "formDefinition": {
                "type": "unknown",
                "mutable": true,
                "complexType": {
                    "original": "FormDefinition",
                    "resolved": "FormDefinition",
                    "references": {
                        "FormDefinition": {
                            "location": "import",
                            "path": "../../utils/FormDefinition",
                            "id": "src/utils/FormDefinition.ts::FormDefinition"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                }
            },
            "action": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "action",
                "reflect": true,
                "defaultValue": "window.location.href"
            },
            "method": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "method",
                "reflect": true,
                "defaultValue": "'post'"
            },
            "submitLabel": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "submit-label",
                "reflect": true,
                "defaultValue": "'Submit'"
            },
            "csrfToken": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string|null",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "csrf-token",
                "reflect": false,
                "defaultValue": "null"
            },
            "initialValue": {
                "type": "unknown",
                "mutable": true,
                "complexType": {
                    "original": "NestedRecord<SubmitField>",
                    "resolved": "{ [key: string]: NestedRecordField<SubmitField>; }",
                    "references": {
                        "NestedRecord": {
                            "location": "import",
                            "path": "../../utils/FormDefinition",
                            "id": "src/utils/FormDefinition.ts::NestedRecord"
                        },
                        "SubmitField": {
                            "location": "import",
                            "path": "../../utils/FormDefinition",
                            "id": "src/utils/FormDefinition.ts::SubmitField"
                        }
                    }
                },
                "required": true,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                }
            },
            "value": {
                "type": "unknown",
                "mutable": true,
                "complexType": {
                    "original": "NestedRecord<SubmitField>",
                    "resolved": "{ [key: string]: NestedRecordField<SubmitField>; }",
                    "references": {
                        "NestedRecord": {
                            "location": "import",
                            "path": "../../utils/FormDefinition",
                            "id": "src/utils/FormDefinition.ts::NestedRecord"
                        },
                        "SubmitField": {
                            "location": "import",
                            "path": "../../utils/FormDefinition",
                            "id": "src/utils/FormDefinition.ts::SubmitField"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "defaultValue": "{}"
            },
            "internalState": {
                "type": "unknown",
                "mutable": true,
                "complexType": {
                    "original": "NestedRecord<Primitive>",
                    "resolved": "{ [key: string]: NestedRecordField<Primitive>; }",
                    "references": {
                        "NestedRecord": {
                            "location": "import",
                            "path": "../../utils/FormDefinition",
                            "id": "src/utils/FormDefinition.ts::NestedRecord"
                        },
                        "Primitive": {
                            "location": "import",
                            "path": "../../utils/FormDefinition",
                            "id": "src/utils/FormDefinition.ts::Primitive"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "defaultValue": "{}"
            },
            "validationErrors": {
                "type": "unknown",
                "mutable": true,
                "complexType": {
                    "original": "NestedRecord<string>",
                    "resolved": "{ [key: string]: NestedRecordField<string>; }",
                    "references": {
                        "NestedRecord": {
                            "location": "import",
                            "path": "../../utils/FormDefinition",
                            "id": "src/utils/FormDefinition.ts::NestedRecord"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "defaultValue": "{}"
            },
            "definitionId": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "definition-id",
                "reflect": true
            },
            "supportsMultipart": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "supports-multipart",
                "reflect": true,
                "defaultValue": "false"
            },
            "debugMode": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "debug-mode",
                "reflect": true,
                "defaultValue": "false"
            },
            "renderInfo": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "RenderInfo",
                    "resolved": "RenderInfo",
                    "references": {
                        "RenderInfo": {
                            "location": "import",
                            "path": "../../utils/RenderInfo",
                            "id": "src/utils/RenderInfo.tsx::RenderInfo"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "defaultValue": "new FallbackRenderInfo()"
            },
            "polymorphicColumnName": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "polymorphic-column-name",
                "reflect": true,
                "defaultValue": "undefined"
            },
            "polymorphicFormDefinition": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "Record<string, string>",
                    "resolved": "{ [x: string]: string; }",
                    "references": {
                        "Record": {
                            "location": "global",
                            "id": "global::Record"
                        }
                    }
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "defaultValue": "undefined"
            }
        };
    }
    static get watchers() {
        return [{
                "propName": "definitionId",
                "methodName": "onDefinitionIdChange"
            }, {
                "propName": "polymorphicFormDefinition",
                "methodName": "updateDefinitionIdOnPolymorphicDefinition"
            }, {
                "propName": "polymorphicColumnName",
                "methodName": "updateDefinitionIdOnPolymorphicDefinition"
            }];
    }
}
//# sourceMappingURL=apie-form.js.map
